import React from "react";
import { useState } from "react";
import { useAuth } from "../../context/auth";
import "./ProfileInformation.css";
import axios from "axios";

import { uploadImageToBlob } from "../../helper/convertToBase64";

const ProfileInformation = ({ user, isEditing, setIsEditing }) => {
  const { updateUser } = useAuth();
  const [formData, setFormData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phoneNumber: user?.phoneNumber || "",
  });

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const [uploading, setUploading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateUser(formData);
      setIsEditing(false);
      setModalType("info");
      setShowSuccessModal(true);
    } catch (error) {
      setModalType("info");
      setShowFailedModal(true);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      phoneNumber: user?.phoneNumber || "",
    });
    setIsEditing(false);
  };

  const handleProfilePhotoClick = () => {
    setShowUploadModal(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert("File size must be below 2MB");
        return;
      }

      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleUploadPicture = async () => {
    if (!selectedFile) return;

    setUploading(true);

    try {
      const base64Image = await convertToBase64(selectedFile);

      const blobResponse = await uploadImageToBlob(
        base64Image,
        `${user?.firstName}-${user?.lastName}`
      );

      const blobUrl = blobResponse?.data?.Location;
      if (!blobUrl) throw new Error("Blob upload failed");

      const response = await axios.patch(
        `/user/${user._id}`,
        { userPhoto: blobUrl },
        { headers: { "Content-Type": "application/json" } }
      );

      const updatedPhoto = response?.data?.data?.userPhoto;

      updateUser({ userPhoto: updatedPhoto });

      setShowUploadModal(false);
      setModalType("picture");
      setShowSuccessModal(true);
      setSelectedFile(null);
      setPreviewUrl("");

      setUploading(false);
    } catch (error) {
      // console.error("Profile photo upload failed:", error);
      setShowUploadModal(false);
      setModalType("picture");
      setShowFailedModal(true);
      setUploading(false);
    }
  };

  const handleBackFromUpload = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    setShowUploadModal(false);
  };

  const closeAllModals = () => {
    setShowSuccessModal(false);
    setShowFailedModal(false);
    setShowUploadModal(false);
    setSelectedFile(null);
    setPreviewUrl("");
  };

  const handleTryAgain = () => {
    setShowFailedModal(false);
    if (modalType === "picture") {
      setShowUploadModal(true);
    } else {
      closeAllModals();
    }
  };

  if (isEditing) {
    return (
      <div className="profile-information edit-mode">
        <h2>Update Personal Information</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter First Name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter Last Name"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="(+234) 000-000-000"
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button type="submit" className="update-button">
              Update
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <>
      <div className="profile-information">
        <div className="profile-photo-section-container">
          <div className="profile-header">
            <h2>Profile Photo</h2>
          </div>

          <div className="profile-photo-section">
            <div className="profile-photo-container">
              <img
                src={user?.userPhoto || "/images/avatar.webp"}
                alt={`${user?.firstName} ${user?.lastName}`}
                className="profile-photo"
              />
              <div
                className="profile-photo-overlay"
                onClick={handleProfilePhotoClick}
              >
                <span className="edit-icon">✏️</span>
              </div>
            </div>
            <div className="profile-name-container">
              <h3>{`${user?.firstName} ${user?.lastName}`}</h3>
              <p>{user?.email}</p>
            </div>
          </div>
        </div>

        <div className="personal-info-section">
          <div className="section-header">
            <h2>Personal Information</h2>
            <button className="edit-button" onClick={() => setIsEditing(true)}>
              <span className="edit-icon">✏️</span>
              Edit
            </button>
          </div>

          <div className="info-grid">
            <div className="info-item">
              <label>First Name</label>
              <p>{user?.firstName}</p>
            </div>
            <div className="info-item">
              <label>Last Name</label>
              <p>{user?.lastName}</p>
            </div>
            <div className="info-item">
              <label>Email Address</label>
              <p>{user?.email}</p>
            </div>
            <div className="info-item">
              <label>Phone number</label>
              <p>{user?.phoneNumber}</p>
            </div>
          </div>
        </div>
      </div>

      {showUploadModal && (
        <div className="profile-picture-modal-overlay">
          <div className="profile-picture-modal">
            <div className="profile-picture-modal-header">
              <button
                className="profile-picture-modal-close"
                onClick={handleBackFromUpload}
              >
                ✕
              </button>
            </div>
            <div className="profile-picture-modal-content">
              <h2>Upload Profile Picture</h2>
              <div className="upload-section">
                <div className="profile-preview">
                  <img
                    src={previewUrl || user?.userPhoto || "/images/avatar.webp"}
                    alt="Profile preview"
                    className="preview-image"
                  />
                </div>
                <div className="upload-info">
                  <p className="upload-label">Change Image</p>
                  <p className="upload-hint">Make sure the file is below 2mb</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="file-input"
                    id="profile-upload"
                  />
                  <label htmlFor="profile-upload" className="file-input-label">
                    Choose File
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button className="back-button" onClick={handleBackFromUpload}>
                  Back
                </button>
                <button
                  className="upload-button"
                  onClick={handleUploadPicture}
                  disabled={!selectedFile || uploading}
                >
                  {uploading ? "Uploading..." : "Upload"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="profile-picture-modal-overlay">
          <div className="profile-picture-modal">
            <div className="profile-picture-modal-header">
              <button
                className="profile-picture-modal-close"
                onClick={closeAllModals}
              >
                ✕
              </button>
            </div>
            <div className="profile-picture-modal-content profile-picture-success-modal">
              <div className="success-icon">
                <img src="/images/approved.png" alt="Logout" />
              </div>
              <h2>Updated</h2>
              <p>
                {modalType === "picture"
                  ? "You have successfully updated your profile picture."
                  : "You have successfully updated your profile information."}
              </p>
              <div className="modal-actions">
                <button className="dismiss-button" onClick={closeAllModals}>
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showFailedModal && (
        <div className="profile-picture-modal-overlay">
          <div className="profile-picture-modal">
            <div className="profile-picture-modal-header">
              <button
                className="profile-picture-modal-close"
                onClick={closeAllModals}
              >
                ✕
              </button>
            </div>
            <div className="profile-picture-modal-content profile-picture-failed-modal">
              <div className="failed-icon">
                <img src="/images/failed.png" alt="Logout" />
              </div>
              <h2>Failed</h2>
              <p>
                {modalType === "picture"
                  ? "Profile picture upload failed, try again."
                  : "Profile information update failed, try again."}
              </p>
              <div className="modal-actions">
                <button className="close-button" onClick={closeAllModals}>
                  Close
                </button>
                <button className="try-again-button" onClick={handleTryAgain}>
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileInformation;
